/**
 * Create and edit a client.
 *
 * The error handling is the substance. The API returns field-level detail on a
 * validation failure, and this maps it onto the form so the wrong input is
 * highlighted — rather than showing a banner and leaving the user to find which
 * of fourteen fields it meant.
 *
 * Money is entered as text and sent as text. A number input would hand the
 * value to the browser's float parsing on the way out, which is the one thing
 * the whole money discipline exists to prevent.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  ApiError,
  Button,
  Input,
  Modal,
  Select,
  Switch,
  Textarea,
  api,
  cn,
  toast,
  useHasService,
} from '@connect/web-shared';

/**
 * The form's own rules.
 *
 * A deliberate subset of the server's: this catches what a user can fix without
 * a round trip. It does **not** try to mirror every server rule — a client-side
 * copy of a business rule is a second place for it to be wrong, and the server
 * refuses anything this misses anyway.
 */
const schema = z.object({
  name: z.string().trim().min(1, 'A client needs a name.').max(255),
  trading_name: z.string().trim().max(255).optional(),
  status: z.enum(['prospect', 'active', 'on_hold', 'former']),
  email: z.union([z.literal(''), z.email('That does not look like an email address.')]).optional(),
  phone: z.string().trim().max(32).optional(),
  website: z.string().trim().max(255).optional(),
  city: z.string().trim().max(120).optional(),
  country: z
    .string()
    .trim()
    .max(2)
    .regex(/^[A-Za-z]{0,2}$/, 'Use the two-letter country code.')
    .optional(),
  tax_id: z.string().trim().max(64).optional(),
  address: z.string().trim().optional(),
  // Text, not a number. See the header.
  default_hourly_rate: z
    .string()
    .trim()
    .regex(/^\d*\.?\d{0,2}$/, 'Enter an amount, to two decimal places.')
    .optional(),
  currency: z.string().trim().length(3, 'Use the three-letter currency code.'),
  // `z.number()`, not `z.coerce.number()`. Coercion makes the schema's INPUT
  // type `unknown` while `z.infer` reports the OUTPUT type, so the resolver and
  // the form's value type disagree and nothing typechecks. The conversion is
  // done at the input instead, with `valueAsNumber`.
  payment_terms_days: z.number().int().min(0, 'Cannot be negative.').max(365, 'At most 365 days.'),
  notes: z.string().trim().optional(),
  portal_enabled: z.boolean(),
  // Kept by the API as the client's primary contact, not on the client itself.
  contact_person: z.string().trim().max(120).optional(),
  contact_mobile: z.string().trim().max(32).optional(),
});

type FormValues = z.infer<typeof schema>;

/** A client as the API sends it: every optional field may be `null`. */
export type ClientRecord = { [K in keyof FormValues]: FormValues[K] | null } & {
  public_id: string;
  code: string;
  name: string;
};

const STATUS_OPTIONS = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'former', label: 'Former' },
];

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD'].map((code) => ({
  value: code,
  label: code,
}));

const DEFAULTS: FormValues = {
  name: '',
  trading_name: '',
  status: 'prospect',
  email: '',
  phone: '',
  website: '',
  city: '',
  country: '',
  tax_id: '',
  address: '',
  default_hourly_rate: '',
  currency: 'INR',
  payment_terms_days: 30,
  notes: '',
  portal_enabled: false,
  contact_person: '',
  contact_mobile: '',
};

/**
 * A stored client as form values.
 *
 * The API sends an empty optional field as `null`; the form's inputs hold
 * text. Spreading the record over the defaults copied every `null` across, so
 * a client saved without a website, a tax number or a rate could not be edited
 * — each of those fields reported "expected string, received null". Every
 * `null` becomes the empty string here, the one place a record enters the
 * form; saving turns empty strings back into `null`.
 */
export function toFormValues(record: ClientRecord): FormValues {
  const values = { ...DEFAULTS };
  for (const key of Object.keys(DEFAULTS) as (keyof FormValues)[]) {
    const value = record[key];
    if (value !== null && value !== undefined) (values as Record<string, unknown>)[key] = value;
  }
  return values;
}

export function ClientFormDialog({
  open,
  onClose,
  onCreated,
  /** Editing an existing client. Omit to create. */
  publicId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (publicId: string) => void;
  publicId?: string;
}) {
  const queryClient = useQueryClient();
  const editing = Boolean(publicId);
  const hasTimeTracking = useHasService('timesheets');

  const existing = useQuery({
    queryKey: ['crm', 'clients', publicId],
    queryFn: () => api.get<ClientRecord>(`/crm/clients/${publicId}`),
    enabled: open && editing,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  // Reset when the dialog opens, and again when an edit's data arrives. Without
  // the reset, reopening shows whatever was half-typed last time.
  useEffect(() => {
    if (!open) return;
    if (editing && existing.data) {
      form.reset(toFormValues(existing.data));
    } else if (!editing) {
      form.reset(DEFAULTS);
    }
  }, [open, editing, existing.data, form]);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      // Empty strings become null rather than being sent as "". The server
      // stores what it is sent, and "" in an email column is a value that
      // fails every later validation.
      const payload = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          typeof value === 'string' && value.trim() === '' ? null : value,
        ]),
      );
      if (editing) {
        return api.patch<ClientRecord>(`/crm/clients/${publicId}`, payload);
      }
      return api.post<ClientRecord>('/crm/clients', payload);
    },
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ['crm', 'clients'] });
      toast.success(editing ? `${record.name} updated` : `${record.name} added`, `Code ${record.code}`);
      onCreated?.(record.public_id);
      if (editing) onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        // Field detail goes onto the fields. This is what makes the API's
        // `error.details` array worth returning.
        const fieldErrors = error.fieldErrors;
        let placed = false;
        for (const [field, message] of Object.entries(fieldErrors)) {
          if (field in DEFAULTS) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        // A refusal with no field — a duplicate name, a dependency — has no
        // input to attach to, so it goes on the form as a whole.
        if (!placed) {
          form.setError('root', { message: error.message });
        }
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const rootError = form.formState.errors.root?.message;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit client' : 'New client'}
      description={
        editing
          ? undefined
          : 'The code is generated automatically. Only the name is required — the rest can be filled in later.'
      }
      size="lg"
      // A half-filled form must not be lost to a stray backdrop click.
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={save.isPending}
            onClick={() => void form.handleSubmit((values) => save.mutateAsync(values))()}
          >
            {editing ? 'Save changes' : 'Create client'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit((values) => save.mutateAsync(values))();
        }}
      >
        {rootError ? (
          <p className="rounded-card border border-danger/25 bg-danger-subtle p-2.5 text-sm text-danger" role="alert">
            {rootError}
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Legal name"
            required
            containerClassName="sm:col-span-2"
            hint="As registered — on invoices and agreements."
            error={form.formState.errors.name?.message}
            {...form.register('name')}
          />
          <Input
            label="Contact person"
            hint="Saved as the client's primary contact."
            error={form.formState.errors.contact_person?.message}
            {...form.register('contact_person')}
          />
          <Input
            label="Mobile number"
            inputMode="tel"
            error={form.formState.errors.contact_mobile?.message}
            {...form.register('contact_mobile')}
          />
          <Input
            label="Trading name"
            hint="Only if they are known by something other than their legal name."
            error={form.formState.errors.trading_name?.message}
            {...form.register('trading_name')}
          />
          <Select
            label="Status"
            options={STATUS_OPTIONS}
            error={form.formState.errors.status?.message}
            {...form.register('status')}
          />
          <Input
            label="Email"
            type="email"
            error={form.formState.errors.email?.message}
            {...form.register('email')}
          />
          <Input
            label="Phone"
            error={form.formState.errors.phone?.message}
            {...form.register('phone')}
          />
          <Input
            label="Website"
            placeholder="https://"
            error={form.formState.errors.website?.message}
            {...form.register('website')}
          />
          <Input
            label="Tax registration"
            hint="The format is national, so it is not validated here."
            error={form.formState.errors.tax_id?.message}
            {...form.register('tax_id')}
          />
          <Input
            label="City"
            error={form.formState.errors.city?.message}
            {...form.register('city')}
          />
          <Input
            label="Country"
            placeholder="IN"
            hint="Two-letter code."
            error={form.formState.errors.country?.message}
            {...form.register('country')}
          />
          <Textarea
            label="Address"
            containerClassName="sm:col-span-2"
            error={form.formState.errors.address?.message}
            {...form.register('address')}
          />
        </div>

        <div
          className={cn(
            'grid gap-4 border-t border-line pt-4',
            hasTimeTracking ? 'sm:grid-cols-3' : 'sm:grid-cols-2',
          )}
        >
          {/* Only time tracking reads it: logged hours on this client's
              projects are valued at this rate unless the project has its own.
              Without that service it would be a number nothing uses. */}
          {hasTimeTracking ? (
            <Input
              label="Hourly rate"
              inputMode="decimal"
              placeholder="0.00"
              hint="Values time logged on this client's projects."
              error={form.formState.errors.default_hourly_rate?.message}
              {...form.register('default_hourly_rate')}
            />
          ) : null}
          <Select
            label="Currency"
            options={CURRENCIES}
            error={form.formState.errors.currency?.message}
            {...form.register('currency')}
          />
          <Input
            label="Payment terms"
            type="number"
            suffix={<span className="text-2xs">days</span>}
            error={form.formState.errors.payment_terms_days?.message}
            {...form.register('payment_terms_days', { valueAsNumber: true })}
          />
        </div>

        <div className="border-t border-line pt-4">
          <Switch
            label="Client portal access"
            hint="Lets people at this client sign in to the client app to see their own tickets and invoices."
            {...form.register('portal_enabled')}
          />
        </div>

        <Textarea label="Notes" rows={3} {...form.register('notes')} />
      </form>
    </Modal>
  );
}
